<script setup lang="ts">
import { computed } from 'vue';
import Icon from '../icon/Icon.vue';
import Badge from '../Badge/Badge.vue';
import type { IconName } from '../icon/Icon.types';

export interface ListItem {
  id?: string | number;
  title?: string;
  description?: string;
  icon?: IconName;
  image?: string;
  badge?: string | number;
  actionLabel?: string;
  disabled?: boolean;
  active?: boolean;
  clickable?: boolean;
}

export interface ListElementClasses {
  root?: string;
  item?: string;
  title?: string;
  description?: string;
  icon?: string;
  image?: string;
  action?: string;
  empty?: string;
  emptyTitle?: string;
  emptyDescription?: string;
}

export interface ListProps {
  items?: ListItem[];
  variant?: 'default' | 'bordered' | 'ghost';
  rounded?: 'none' | 'sm' | 'md' | 'lg' | 'xl';
  divided?: boolean;
  hoverable?: boolean;
  selectable?: boolean;
  modelValue?: string | number | null;
  customClass?: string;
  elementClasses?: ListElementClasses;
}

const props = withDefaults(defineProps<ListProps>(), {
  items: () => [],
  variant: 'default',
  rounded: 'xl',
  divided: true,
  hoverable: true,
  selectable: false,
  modelValue: null,
  elementClasses: () => ({}),
});

const defaultElementClasses: Required<ListElementClasses> = {
  root: 'overflow-hidden space-y-3 p-3',
  item: 'text-primary',
  title: 'truncate font-medium text-primary',
  description: 'mt-1 text-sm text-on-surface-muted',
  icon: 'bg-primary/10 h-8 w-8 flex justify-center items-center rounded text-primary',
  image: 'bg-primary-light rounded w-28',
  action: 'rounded-lg border border-primary-light px-3 py-1.5 text-sm transition hover:bg-surface-muted',
  empty: 'flex flex-col items-center justify-center px-6 py-12 text-center',
  emptyTitle: 'font-medium text-primary',
  emptyDescription: 'mt-1 text-sm text-on-surface-muted',
};

const classesFor = (key: keyof ListElementClasses): string =>
  [defaultElementClasses[key], props.elementClasses?.[key]].filter(Boolean).join(' ');

const emit = defineEmits<{
  select: [item: ListItem];
  action: [item: ListItem];
  'update:modelValue': [value: string | number | null];
}>();

const variantClasses = computed(() => {
  return {
    default: 'bg-surface border border-primary-light  shadow-md',

    bordered: 'bg-transparent border border-primary-light',

    ghost: 'bg-gray-50 border-transparent  shadow-md',
  }[props.variant];
});

const roundedClasses = computed(() => {
  return {
    none: 'rounded-none',
    sm: 'rounded-sm',
    md: 'rounded-md',
    lg: 'rounded-lg',
    xl: 'rounded-xl',
  }[props.rounded];
});

const selectItem = (item: ListItem) => {
  if (item.disabled) return;

  if (props.selectable) {
    emit('update:modelValue', item.id ?? null);
  }

  emit('select', item);
};
</script>

<template>
  <div :class="classesFor('root')">
    <button
      v-for="(item, index) in items"
      :key="item.id ?? index"
      type="button"
      :disabled="item.disabled"
      :class="[
        'w-full flex items-cstart gap-4 text-left transition h-24',
        classesFor('item'),
        customClass,
        roundedClasses,
        variantClasses,
        {
          'border-b border-primary-light': divided,

          'hover:bg-surface-muted': hoverable && item.clickable,

          'bg-primary/10': item.active || modelValue === item.id,

          'cursor-pointer': item.clickable,

          'opacity-50 pointer-events-none': item.disabled,
        },
      ]"
      @click="selectItem(item)"
    >
      <!-- Image -->
      <div v-if="item.image" :class="classesFor('image')">
        <img :src="item.image" class="h-full w-full rounded object-cover" />
      </div>

      <!-- Icon -->
      <div v-else-if="item.icon" :class="classesFor('icon')">
        <Icon :name="item.icon" size="sm" />
      </div>

      <!-- Content -->
      <div class="min-w-0 flex-1">
        <div class="flex items-center gap-2">
          <h3 :class="classesFor('title')">
            {{ item.title }}
          </h3>

          <!-- Badge -->
          <Badge v-if="item.badge" variant="success" type="basic" :label="item.badge.toString()" />
        </div>

        <p v-if="item.description" :class="classesFor('description')">
          <slot name="description" :item="item">
            {{ item.description }}
          </slot>
        </p>
      </div>

      <!-- Action -->
      <button
        v-if="item.actionLabel"
        :class="classesFor('action')"
        @click.stop="$emit('action', item)"
      >
        {{ item.actionLabel }}
      </button>
    </button>

    <!-- Empty -->
    <div v-if="!items.length" :class="classesFor('empty')">
      <h3 :class="classesFor('emptyTitle')">No items found</h3>

      <p :class="classesFor('emptyDescription')">List is currently empty.</p>
    </div>
  </div>
</template>
